import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { useCage } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import {
    type CreateSmoltReportBody,
    type CreatingReportPlaceholder,
    type SmoltReportListItemRow,
    useDeleteReport,
    useGetListReports,
    usePostCreateReport,
} from '@/domain/smoltReport';
import useGoToUrl from '@/hooks/useGoToUrl';
import { isBwBioscopeVersion } from '@/utils/bioscopeVersionHelpers';

import SmoltReportList from './SmoltReportList';

const EMPTY_WARNINGS: CreatingReportPlaceholder['warnings'] = {
    weightLowFrequency: false,
    weightDataLatency: false,
    dataEndsBeforeDelivery: false,
    weightPeriodTooShort: false,
    welfareLowCoverage: false,
    welfareInsufficientSample: false,
};

function buildCreatingPlaceholder(
    cageId: number,
    fishGroupId: number | null,
    body: CreateSmoltReportBody
): CreatingReportPlaceholder {
    return {
        id: 'creating',
        status: 'created',
        cageId,
        fishGroupId,
        stockingDate: body.stockingDate,
        lastFeedingDate: body.lastFeedingDate ?? null,
        deliveryDate: body.deliveryDate,
        createdAt: null,
        warnings: EMPTY_WARNINGS,
    };
}

interface SmoltReportPageContainerProps {
    cageId: number;
}

export default function SmoltReportPageContainer(props: SmoltReportPageContainerProps) {
    const { cageId } = props;
    const goToUrl = useGoToUrl();
    const { selectedFishGroupId } = useCageFilter();
    const { bioscope } = useCage();
    const { t } = useTranslation('smoltReport');

    const [creatingReport, setCreatingReport] = useState<CreateSmoltReportBody | null>(null);
    const [deletingReportId, setDeletingReportId] = useState<number | null>(null);

    const { data: reports, isLoading, refetch } = useGetListReports(cageId);
    const { mutateAsync: createReport } = usePostCreateReport();
    // `mutate` reports a failure through onError instead of rejecting, as nothing awaits the delete.
    const { mutate: deleteReport } = useDeleteReport({
        onSuccess: () => refetch(),
        onError: (error) =>
            ToastError(
                apiErrorContent(error, t('reportList.deleteFailed')),
                'smolt-report-delete-error'
            ),
        onMutate: (variables) => setDeletingReportId(variables.reportId),
        onSettled: () => setDeletingReportId(null),
    });

    // The create dialog has closed by now, so a failure is told in a toast.
    const handleCreateReport = async (body: CreateSmoltReportBody) => {
        setCreatingReport(body);
        try {
            await createReport({ cageId, requestBody: body, fishGroupId: selectedFishGroupId });
            analytics.reportCreated({ reportType: 'smolt' });
            await refetch();
        } catch (error: unknown) {
            ToastError(
                apiErrorContent(error, t('createSmoltReport.createFailed')),
                'smolt-report-create-error'
            );
        } finally {
            setCreatingReport(null);
        }
    };

    const listItemRows: SmoltReportListItemRow[] = creatingReport
        ? [
              buildCreatingPlaceholder(cageId, selectedFishGroupId ?? null, creatingReport),
              ...(reports ?? []),
          ]
        : (reports ?? []).map((report) => ({
              ...report,
              onClick: () => {
                  goToUrl(`/${report.id}`);
              },
              onDelete: () => {
                  deleteReport({ cageId, reportId: report.id });
              },
              isDeleting: report.id === deletingReportId,
          }));

    return (
        <SmoltReportList
            isLoading={isLoading}
            reports={listItemRows}
            isFishGroupSelected={selectedFishGroupId != null}
            canCreate={!isBwBioscopeVersion(bioscope?.version)}
            onCreateReport={handleCreateReport}
        />
    );
}
